import Vue from "vue";
import Vuex from "vuex";
import { fetchCourse,
  fetchCourseProgress,
  fetchCourseReleases,
  fetchCourseValidity,
  updateCourseProgress } from "@/core/api";

Vue.use(Vuex);

const DEFAULT_LESSON = {
  questoes: [
  ],
};

const emptyModuleMeta = () => ({
  title: "",
  description: "",
});

const buildFallbackContentId = (moduleIndex, lessonIndex) => `generated-${moduleIndex + 1}-${lessonIndex + 1}`;

const normalizeModules = (modules = [
]) => {
  const normalizedModules = [
  ];
  const lessons = [
  ];
  const lessonByContentId = {
  };
  const lessonIndexByContentId = {
  };
  const moduleMetaByLessonId = {
  };

  modules.forEach((module, moduleIndex) => {
    const content = module?.conteudo_do_modulo ?? [
    ];
    const normalizedContent = content.map((lesson, lessonIndex) => {
      const contentId = lesson?.content_id ?? buildFallbackContentId(moduleIndex, lessonIndex);
      const normalizedLesson = {
        ...lesson,
        content_id: contentId,
      };

      lessonByContentId[contentId] = normalizedLesson;
      lessonIndexByContentId[contentId] = lessons.length;
      moduleMetaByLessonId[contentId] = {
        title: module?.nome_modulo ?? "",
        description: module?.descricao_do_modulo ?? "",
      };
      lessons.push(normalizedLesson);

      return normalizedLesson;
    });

    normalizedModules.push({
      ...module,
      conteudo_do_modulo: normalizedContent,
    });
  });

  return {
    modules: normalizedModules,
    lessons,
    lessonByContentId,
    lessonIndexByContentId,
    moduleMetaByLessonId,
  };
};

const normalizeProgress = (lessons = [
]) => lessons.reduce((progressByContentId, item) => {
  if (!item?.content_id) {
    return progressByContentId;
  }

  progressByContentId[item.content_id] = {
    ...item,
  };

  return progressByContentId;
}, {
});

const normalizeReleases = (releases = [
]) => releases.reduce((releaseByContentId, item) => {
  if (!item?.content_id) {
    return releaseByContentId;
  }

  releaseByContentId[item.content_id] = {
    ...item,
  };

  return releaseByContentId;
}, {
});

const resolveInitialLessonId = (state, lastLessonId) => {
  if (lastLessonId && state.lessonByContentId[lastLessonId]) {
    return lastLessonId;
  }

  return state.lessons[0]?.content_id ?? null;
};

export default new Vuex.Store({
  state: {
    course: {
      id: "",
      title: "",
    },
    modules: [
    ],
    lessons: [
    ],
    lessonByContentId: {
    },
    lessonIndexByContentId: {
    },
    moduleMetaByLessonId: {
    },
    currentLessonId: null,
    progressByContentId: {
    },
    releaseByContentId: {
    },
    validity: false,
  },
  getters: {
    getCourse: (state) => state.course,
    getModules: (state) => state.modules,
    getLesson: (state) => state.lessonByContentId[state.currentLessonId] ?? DEFAULT_LESSON,
    getIsLastLesson: (state) => {
      const lessonIndex = state.lessonIndexByContentId[state.currentLessonId];

      return Number.isInteger(lessonIndex) && lessonIndex === state.lessons.length - 1;
    },
    getCourseProgress: (state) => Object.values(state.progressByContentId),
    getLessonProgress: (state) => state.progressByContentId[state.currentLessonId] ?? {
    },
    getProgressByContentId: (state) => (contentId) => state.progressByContentId[contentId] ?? {
    },
    getCurrentModule: (state) => state.moduleMetaByLessonId[state.currentLessonId] ?? emptyModuleMeta(),
    getPreviousLesson: (state) => {
      const lessonIndex = state.lessonIndexByContentId[state.currentLessonId];

      if (Number.isInteger(lessonIndex) && lessonIndex > 0) {
        return state.lessons[lessonIndex - 1];
      }

      return null;
    },
    getNextLesson: (state) => {
      const lessonIndex = state.lessonIndexByContentId[state.currentLessonId];

      if (Number.isInteger(lessonIndex) && lessonIndex < state.lessons.length - 1) {
        return state.lessons[lessonIndex + 1];
      }

      return null;
    },
    getIsFinishedCourse: (state) => state.lessons.length > 0 && state.lessons.every(
      ({ content_id: contentId }) => state.progressByContentId[contentId]?.completed,
    ),
    getReleaseByContentId: (state) => (contentId) => state.releaseByContentId[contentId] ?? null,
    getCurrentLessonRelease: (state) => state.releaseByContentId[state.currentLessonId] ?? null,
    getCourseValidity: (state) => state.validity,
  },
  mutations: {
    setCourse(state, payload) {
      const { title, acf, id } = payload;
      const modules = acf?.modulos ?? [
      ];
      const normalizedModules = normalizeModules(modules);

      state.course = {
        title: title.rendered,
        id,
      };
      state.modules = normalizedModules.modules;
      state.lessons = normalizedModules.lessons;
      state.lessonByContentId = normalizedModules.lessonByContentId;
      state.lessonIndexByContentId = normalizedModules.lessonIndexByContentId;
      state.moduleMetaByLessonId = normalizedModules.moduleMetaByLessonId;

      if (!state.lessonByContentId[state.currentLessonId]) {
        state.currentLessonId = state.lessons[0]?.content_id ?? null;
      }
    },
    setCurrentLessonId(state, lessonId) {
      if (!state.lessonByContentId[lessonId]) {
        return;
      }

      state.currentLessonId = lessonId;
    },
    setCourseProgress(state, progress) {
      state.progressByContentId = normalizeProgress(progress?.lessons);
      state.currentLessonId = resolveInitialLessonId(state, progress?.last_lesson);
    },
    mergeLessonProgress(state, payload) {
      const currentProgress = state.progressByContentId[payload.content_id] ?? {
      };

      Vue.set(state.progressByContentId, payload.content_id, {
        ...currentProgress,
        ...payload,
      });
    },
    setCourseReleases(state, releases) {
      state.releaseByContentId = normalizeReleases(releases?.releases);
    },
    setCourseValidity(state, validity) {
      state.validity = validity || false;
    },
  },
  actions: {
    async initializeCourse({
      commit,
    }, courseId) {
      const [
        course,
        progress,
        releases,
        validity,
      ] = await Promise.all([
        fetchCourse(courseId),
        fetchCourseProgress(courseId).catch(() => ({
          lessons: [
          ],
          last_lesson: null,
        })),
        fetchCourseReleases(courseId).catch(() => ({
          releases: [
          ],
        })),
        fetchCourseValidity(courseId).catch(() => false),
      ]);

      commit("setCourse", course);
      commit("setCourseProgress", progress);
      commit("setCourseReleases", releases);
      commit("setCourseValidity", validity);
    },
    async selectLesson({
      commit,
      getters,
    }, lesson) {
      const lessonId = lesson?.content_id;

      if (!lessonId || lessonId === getters.getLesson.content_id) {
        return;
      }

      commit("setCurrentLessonId", lessonId);
    },
    async saveLessonProgress({
      commit,
      state,
    }, {
      completed,
      progress,
      courseId,
      lessonId = state.currentLessonId,
    } = {}) {
      if (!lessonId) {
        return null;
      }

      const currentProgress = state.progressByContentId[lessonId] ?? {
      };
      const normalizedProgress = progress ?? null;
      const normalizedCompleted = Boolean(completed || currentProgress.completed);
      const payload = {
        content_id: lessonId,
        completed: normalizedCompleted,
        progress: normalizedProgress,
      };

      if (
        currentProgress.progress === payload.progress
        && Boolean(currentProgress.completed) === payload.completed
      ) {
        return currentProgress;
      }

      commit("mergeLessonProgress", payload);

      try {
        await updateCourseProgress(courseId ?? state.course.id, payload);
      } catch {
        // Mantem o estado local consistente mesmo quando o backend oscila.
      }

      return payload;
    },
  },
});
